import React, { ComponentPropsWithRef } from 'react';

import PropTypes from 'prop-types';

import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType } from '@adsk/alloy-react-theme';

import Modal from '../Modal';

type Props = ComponentPropsWithRef<typeof Modal> & {
  className?: string;
  bootstrapModal: {
    onClose?: () => void;
    show?: boolean;
    bsClass?: string;
  };
  onClose?: () => void;
  show?: boolean;
  previous?: () => void;
  next?: () => void;
  navigationEnabled?: boolean;
  disabledNavigationNextArrow?: boolean;
  disabledNavigationPrevArrow?: boolean;
  disabledNavigationArrows?: boolean;
};

/**
 * This component allows for displaying a content in a modal dialog
 * while providing next/previous navigation actions.
 */
const ModalPreviewNavigation = ({
  bootstrapModal = {},
  children,
  className = bootstrapModal.bsClass,
  style,
  show = bootstrapModal.show,
  next,
  previous,
  navigationEnabled = false,
  disabledNavigationArrows,
  disabledNavigationPrevArrow,
  disabledNavigationNextArrow,
  onClose = bootstrapModal.onClose,
  ...props
}: Props) => {
  const theme = useTheme();
  const modalTokens = theme.tokens?.component?.modal;

  return (
    <Modal
      className={className}
      style={style}
      open={show}
      onClose={onClose}
      {...props}
    >
      <Modal.Close
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '0',
          right: '-40px',
          margin: 0,
          color: modalTokens?.arrow?.['icon-color'],
        }}
      />
      {navigationEnabled && (
        <Modal.PrevArrow
          disabled={disabledNavigationArrows || disabledNavigationPrevArrow}
          onClick={previous}
        />
      )}
      {navigationEnabled && (
        <Modal.NextArrow
          disabled={disabledNavigationArrows || disabledNavigationNextArrow}
          onClick={next}
        />
      )}
      {children}
    </Modal>
  );
};

ModalPreviewNavigation.CLOSE_BUTTON_STYLE = {
  DEFAULT: '',
  NO_BUTTON: 'NO_BUTTON',
  INNER_CLOSE_BUTTON: 'INNER_CLOSE_BUTTON',
};

ModalPreviewNavigation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** overwrite default props */
  bootstrapModal: PropTypes.shape({
    /**
     * A callback function for modal close event
     */
    onClose: PropTypes.func,
    /**
     * Whether to show the modal or not
     */
    show: PropTypes.bool,
    /**
     * A custom className for the modal component. This class will be passed to Modal component.
     */
    bsClass: PropTypes.string,
  }).isRequired,
  /**
   * A callback that's called when hiding the modal. (same as `bootstrapModal.onClose` prop)
   */
  onClose: PropTypes.func,
  /**
   * Whether or not to show the modal. (same as `bootstrapModal.show` prop)
   */
  show: PropTypes.bool,
  /**
   * A callback function for previous button click event
   */
  previous: PropTypes.func,
  /**
   * A callback function for next button click event
   */
  next: PropTypes.func,
  /**
   * Determines whether navigation is enabled. If false is passed, navigation is disabled and arrows are not visible. Defaults to false.
   */
  navigationEnabled: PropTypes.bool,
  /**
   * Disables navigation next arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationNextArrow: PropTypes.bool,
  /**
   * Disables navigation back arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationPrevArrow: PropTypes.bool,
  /**
   * Disables navigation next arrow (still shows them, but disabled). Disables navigation
   */
  disabledNavigationArrows: PropTypes.bool,
  /**
   * Modal content
   */
  children: PropTypes.node,
};

export default ModalPreviewNavigation;
